import React, { useState, useEffect } from "react";
import axios from 'axios';
import params from '../config/Params';
import { getItemFromLocalStorage } from '../utils/storageUtils';
import { Helmet } from 'react-helmet';
import { toast } from 'react-toastify';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid } from 'recharts';

const COLORS = ['#5500FF', '#00CC99', '#FFBB28'];

const Earnings = () => {
  const [earnings, setEarnings] = useState([]);
  const [myEarnings, setMyEarnings] = useState(null);
  const [earningLoading, setEarningLoading] = useState(false);
  const [totalEarnings, setTotalEarnings] = useState(0);
  const [weeklyEarnings, setWeeklyEarnings] = useState(0);
  const [monthlyEarnings, setMonthlyEarnings] = useState(0);
  const [totalHostFee, setTotalHostFee] = useState(0);
  const [totalGst, setTotalGst] = useState(0);
  const [activeTab, setActiveTab] = useState('today');
  const [graphTotalEarning, setGraphTotalEarning] = useState('0.00');
  const [showCustomDatePopup, setShowCustomDatePopup] = useState(false);
  const [customDates, setCustomDates] = useState({ from_date: '', to_date: '' });
  const [filters, setFilters] = useState({
    dateFrom: "",
    dateTo: "",
  });

  useEffect(() => {
    fetchEarnings();
  }, [filters]);

  const fetchEarnings = async () => {
    const hostData = getItemFromLocalStorage('hostProfile');
    let formData = new FormData();
    formData.append('token', hostData.host_access_token);
    formData.append('date_from', filters.dateFrom || '');
    formData.append('date_to', filters.dateTo || '');

    try {
      const response = await axios.post(
        `${params.baseURL}${params.endpoints.getEarnings}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      if (response.data.status === 'success') {
        const data = response.data.data;

        const total = data.reduce((sum, item) => sum + parseFloat(item.total_earning || 0), 0);
        const hostFee = data.reduce((sum, item) => sum + parseFloat(item.host_fee || 0), 0);
        const gst = data.reduce((sum, item) => sum + parseFloat(item.gst || 0), 0);

        const week = data.reduce((sum, item) => {
          if (isThisWeek(item.booking_date)) {
            return sum + parseFloat(item.total_earning || 0);
          }
          return sum;
        }, 0);

        const month = data.reduce((sum, item) => {
          if (isThisMonth(item.booking_date)) {
            return sum + parseFloat(item.total_earning || 0);
          }
          return sum;
        }, 0);

        setEarnings(data);
        setTotalEarnings(total);
        setWeeklyEarnings(week);
        setMonthlyEarnings(month);
        setTotalHostFee(hostFee);
        setTotalGst(gst);
      } else {
        toast.error('Unable to fetch earnings. Try again.');
      }
    } catch (error) {
      console.error('Error fetching earnings:', error);
      toast.error('An error occurred while fetching earnings.');
    }
  };

  const handleFilterChange = (e) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value,
    });
  };

  /*const handleSearch = () => {
    fetchEarnings();
  };*/

  const isThisWeek = (date) => {
    const today = new Date();
    const bookingDate = new Date(date);
    const startOfWeek = new Date(today.setDate(today.getDate() - today.getDay()));
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);
    return bookingDate >= startOfWeek && bookingDate <= endOfWeek;
  };

  const isThisMonth = (date) => {
    const today = new Date();
    const bookingDate = new Date(date);
    return bookingDate.getMonth() === today.getMonth() && bookingDate.getFullYear() === today.getFullYear();
  };

  const calculateDaysAgo = (dateString) => {
    if (!dateString) return "-";
    const today = new Date();
    const date = new Date(dateString);
    const diffTime = Math.abs(today - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return `${diffDays} days`;
  };

  const pieData = [
    { name: 'Host Earnings', value: totalEarnings, fill: COLORS[0] },
    { name: 'Platform Charges', value: totalHostFee, fill: COLORS[1] },
    { name: 'Other Charges', value: totalGst, fill: COLORS[2] },
  ];

  const handleEarningGraphChange = async (category) => {
    setActiveTab(category);
    if (category === 'custom') {
      setShowCustomDatePopup(true);
      return;
    }

    fetchGraphEarnings(category);
  };

  const fetchGraphEarnings = async (category, from = '', to = '') => {
    setEarningLoading(true);
    const hostData = getItemFromLocalStorage('hostProfile');

    let formData = new FormData();
    formData.append('token', hostData.host_access_token);
    formData.append('category', category);
    if (from) formData.append('from_date', from);
    if (to) formData.append('to_date', to);

    try {
      const response = await axios({
        method: 'post',
        headers: {
            'Content-Type': 'multipart/form-data'
        },
        url: `${params.baseURL}${params.endpoints.getMyEarnings}`,
        data: formData
      });
      if (response.data.status === 'ok') {
        const transformed = (response.data.earning_list || []).map(item => ({
          ...item,
          value: parseFloat(item.amount),
        }));
        setMyEarnings(transformed);
        setGraphTotalEarning(response.data.earning_total || '0.00');
      } else {
        setMyEarnings([]);
      }
    } catch (error) {
      console.error(error);
      setMyEarnings([]);
    } finally {
      setEarningLoading(false);
    }
  };

  const handleDownloadReport = async () => {
    const hostData = getItemFromLocalStorage("hostProfile");

    let formData = new FormData();
    formData.append("token", hostData.host_access_token);
    formData.append("category", activeTab);
    if (activeTab === "custom") {
      formData.append("from_date", customDates.from_date);
      formData.append("to_date", customDates.to_date);
    }

    try {
      const response = await axios({
        method: 'post',
        headers: {
            'Content-Type': 'multipart/form-data'
        },
        url: `${params.baseURL}${params.endpoints.earningReportLink}`,
        data: formData
      });

      if (response.data.status === "ok" && response.data.earning_report_url) {
        const link = document.createElement('a');
        link.href = response.data.earning_report_url;
        link.setAttribute('download', 'earnings_report.xlsx');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        toast.error("No record found to export.");
      }
    } catch (error) {
      console.error("Report download error", error);
    }
  };

  const tabs = ['today', 'week', 'month', 'year', 'custom'];

  const getXAxisKey = () => {
    if (Array.isArray(myEarnings) && myEarnings.length > 0) {
      const firstItem = myEarnings[0];
      const keys = Object.keys(firstItem);
      return keys.find((key) => key !== 'value') || 'label';
    }
    return 'label';
  };

  return (
    <div>
      <Helmet>
        <title>Earnings</title>
        <meta name="description" content="" />
      </Helmet>

      <div className="earning-section">

        <section class="earning-card" id="total-earning">
          <div class="balance-card">
            <div className="phs-row">
              <div className="row1-col">
                <div className="tmwe-box">
                  {/*<h2 class="earning-label">Total Earnings</h2>*/}
                  <img src="/assets/img/title/total_earning_txt.png" alt="Total Earning" className="img-fluid" />
                  <h4 id="totalBalance">INR {totalEarnings.toFixed(2)}</h4>
                </div>
              </div>
              <div className="row1-col">
                <div className="tmwe-box">
                  <img src="/assets/img/title/monthly_earning_txt.png" alt="Monthly Earning" className="img-fluid" />
                  <h4 id="totalBalance">INR {monthlyEarnings.toFixed(2)}</h4>
                </div>
              </div>
              <div className="row1-col">
                <div className="tmwe-box">
                  <img src="/assets/img/title/pending_earning_txt.png" alt="Pending Earning" className="img-fluid" />
                  <h4 id="totalBalance">INR {weeklyEarnings.toFixed(2)}</h4>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="pay-history-section chart-section">
          <div className="phs-row">
            <div className="row2-col1 phs-box">
              <div style={{ width: '100%', maxWidth: '100%' }}>
      
                <div style={{
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  borderRadius: '10px',
                  padding: '20px',
                  marginBottom: '1rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '15px'
                }}>
                  <img src="/assets/img/icons/menu_icon.svg" alt="Earnings Icon" style={{ width: '40px', height: '40px' }} />
                  <div>
                    <div style={{ fontSize: '24px', fontWeight: 'bold' }}>₹ {graphTotalEarning}</div>
                    <div style={{ color: '#666' }}>{activeTab === 'month' ? 'This Month' : activeTab.replace(/^\w/, c => c.toUpperCase())}</div>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', justifyContent: 'center', flexWrap: 'wrap' }}>
                  {tabs.map((tab) => (
                    <button
                      key={tab}
                      onClick={() => handleEarningGraphChange(tab)}
                      style={{
                        flex: 1,
                        padding: '8px 12px',
                        background: activeTab === tab ? '#1890ff' : '#f0f0f0',
                        color: activeTab === tab ? '#fff' : '#000',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        minWidth: '100px',
                        textTransform: 'capitalize',
                      }}
                    >
                      {tab === 'week' ? 'This Week' :
                        tab === 'month' ? 'This Month' :
                        tab === 'year' ? 'This Year' :
                        tab === 'custom' ? 'Custom' :
                        'Today'}
                    </button>
                  ))}
                </div>

                {earningLoading ? (
                <div style={{ textAlign: 'center', padding: '2rem' }}>Loading chart...</div>
                ) : !Array.isArray(myEarnings) || myEarnings.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '2rem' }}>No data available</div>
                ) : (
                  <ResponsiveContainer width="100%" height={300}>
                    <AreaChart data={myEarnings} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                      <defs>
                        <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#1890ff" stopOpacity={0.5} />
                          <stop offset="95%" stopColor="#1890ff" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="datetime" />
                      <Tooltip />
                      <Area type="monotone" dataKey="value" stroke="#1890ff" strokeWidth={3} fillOpacity={1} fill="url(#colorValue)" />
                    </AreaChart>
                  </ResponsiveContainer>
                )}

                <div style={{ marginTop: '2rem' }}>
                  {Array.isArray(myEarnings) && myEarnings.length > 0 && myEarnings.map((item, idx) => (
                    <div key={idx} style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                      <input type="text" value={item.datetime} readOnly style={{ flex: 1, padding: '6px', borderRadius: '4px', border: '1px solid #ccc' }} />
                      <input type="text" value={item.description} readOnly style={{ flex: 1, padding: '6px', borderRadius: '4px', border: '1px solid #ccc' }} />
                      <input type="text" value={`₹ ${item.amount}`} readOnly style={{ flex: 1, padding: '6px', borderRadius: '4px', border: '1px solid #ccc' }} />
                    </div>
                  ))}
                </div>

                <button
                  onClick={handleDownloadReport}
                  style={{
                    marginTop: '1.5rem',
                    background: '#0066ff',
                    color: '#fff',
                    padding: '12px',
                    width: '100%',
                    border: 'none',
                    borderRadius: '6px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    fontSize: '16px',
                  }}
                >
                  DOWNLOAD REPORT
                </button>
              </div>
            </div>
            <div className="row2-col2 phs-box">
              <div className="chart-section">
                <h2 className="tnstxt">Commission Breakdown</h2>
                <div style={{ width: '100%', height: 400 }}>
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie
                        data={pieData}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        label
                      >
                        {pieData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>

                <div style={{ flex: '1 1 200px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  {pieData.map((entry, index) => (
                    <div key={index} style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
                      <div
                        style={{
                          width: 20,
                          height: 20,
                          backgroundColor: entry.fill,
                          borderRadius: '50%',
                          border: '1px solid #ccc',
                          marginRight: 10,
                        }}
                      ></div>
                      <span style={{ fontWeight: 'bold', marginRight: 6, color: entry.fill, textTransform: 'uppercase' }}>{entry.name}</span>
                    </div>
                  ))}
                </div>

              </div>
            </div>
          </div>
        </section>

        <section className="pay-history-section">
          <div>
            <div className="phs-box w-100">
              <div className="phsvb">
                <h2 className="tnstxt">Transaction History</h2>
                <div className="table-flex">
                  <div className="table-row table-header">
                    <div className="table-cell" style={{width:'250px'}}>
                      <img src="/assets/img/icons/common_point_icon.png" alt="pointer" className="pointer-icon tb" />
                      PROPERTY NAME
                    </div>
                    <div className="table-cell">
                      <img src="/assets/img/icons/common_point_icon.png" alt="pointer" className="pointer-icon tb" />
                      AMOUNT
                    </div>
                    <div className="table-cell">
                      <img src="/assets/img/icons/common_point_icon.png" alt="pointer" className="pointer-icon tb" />
                      DATE
                    </div>
                    <div className="table-cell">
                      <img src="/assets/img/icons/common_point_icon.png" alt="pointer" className="pointer-icon tb" />
                      STATUS
                    </div>
                  </div>

                  {earnings.length > 0 ? (
                    earnings.map((earning, index) => (
                      <div className="table-row" key={index}>
                        <div className="table-cell" style={{width:'250px', textTransform:'uppercase'}}>
                          <img src="/assets/img/icons/common_point_icon.png" alt="pointer" className="pointer-icon tb" />
                          {earning.property_name
                            ? earning.property_name.length > 15
                              ? earning.property_name.substring(0, 12) + "..."
                              : earning.property_name
                            : "-"}
                        </div>
                        <div className="table-cell">
                          <img src="/assets/img/icons/common_point_icon.png" alt="pointer" className="pointer-icon tb" />
                          INR {parseFloat(earning.total_earning || 0).toFixed(2)}
                        </div>
                        <div className="table-cell">
                          <img src="/assets/img/icons/common_point_icon.png" alt="pointer" className="pointer-icon tb" />
                          {earning.booking_date}
                        </div>
                        <div className="table-cell">
                          <img src="/assets/img/icons/common_point_icon.png" alt="pointer" className="pointer-icon tb" />
                          <span className="status processed">{earning.status || "COMPLETED"}</span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="table-row">
                      <div className="table-cell text-center" style={{ flex: '1 0 100%' }}>
                        No earnings found.
                      </div>
                    </div>
                  )}
                </div>
                <div className="tnsbtn-box">
                  <a href="earning-details">View Details</a>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="pay-history-section">
          <div className="phs-row">
            <div className="row2-col1 phs-box">
              <div className="phsvb">
                <h2 className="tnstxt">Payment History</h2>
                <div className="table-flex">
                  <div className="table-row table-header">
                    <div className="table-cell" style={{width:'250px'}}>
                      <img src="/assets/img/icons/common_point_icon.png" alt="pointer" className="pointer-icon tb" />
                      DATE
                    </div>
                    <div className="table-cell">
                      <img src="/assets/img/icons/common_point_icon.png" alt="pointer" className="pointer-icon tb" />
                      AMOUNT
                    </div>
                    <div className="table-cell">
                      <img src="/assets/img/icons/common_point_icon.png" alt="pointer" className="pointer-icon tb" />
                      TRANSACTION ID
                    </div>
                    <div className="table-cell">
                      <img src="/assets/img/icons/common_point_icon.png" alt="pointer" className="pointer-icon tb" />
                      STATUS
                    </div>
                  </div>

                  {earnings.length > 0 ? (
                    earnings.slice(-2).map((earning, index) => (
                      <div className="table-row" key={index}>
                        <div className="table-cell" style={{ width: '250px', textTransform: 'uppercase' }}>
                          <img src="/assets/img/icons/common_point_icon.png" alt="pointer" className="pointer-icon tb" />
                          {earning.booking_date}
                        </div>
                        <div className="table-cell">
                          <img src="/assets/img/icons/common_point_icon.png" alt="pointer" className="pointer-icon tb" />
                          INR {parseFloat(earning.total_earning || 0).toFixed(2)}
                        </div>
                        <div className="table-cell">
                          <img src="/assets/img/icons/common_point_icon.png" alt="pointer" className="pointer-icon tb" />
                          setl_IJQt3WHfsBbhTE
                        </div>
                        <div className="table-cell">
                          <img src="/assets/img/icons/common_point_icon.png" alt="pointer" className="pointer-icon tb" />
                          <span className="status processed">{earning.status || "COMPLETED"}</span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="table-row">
                      <div className="table-cell text-center" style={{ flex: '1 0 100%' }}>
                        No earnings found.
                      </div>
                    </div>
                  )}
                </div>
                <div className="tnsbtn-box">
                  <a href="settlement-details">View Details</a>
                </div>
              </div>
            </div>
            <div className="row2-col2 phs-box espmbx-section">
              <div className="chart-section">
                <h2 className="tnstxt">SUPPORT</h2>
                <div style={{ width: '100%' }}>
                  <a href="mailto:support@wirestay.com" className="espmbx">
                    <i class="fas fa-envelope"></i>
                    <span>support@wirestay.com</span>
                  </a> 
                </div>
              </div>  
            </div>
          </div>
        </section>

      </div>

      {showCustomDatePopup && (
        <div className="confirmation-popup-overlay">
          <div className="confirmation-popup">
            <div className="popup-body mb-4" style={{ position: 'relative' }}>
              <div className="euc-close" style={{top:'-6px'}} onClick={() => setShowCustomDatePopup(false)}>
                <img src={'/assets/img/icons/popup_close.png'} className="img-fluid" alt="" style={{width:'30px'}} />
              </div>
              <h4>Select Custom Date Range</h4>
            </div>
            
            <div className="form-floating mb-4">
              <input
                type="date"
                className="form-control"
                value={customDates.from_date}
                onChange={(e) => setCustomDates({ ...customDates, from_date: e.target.value })}
                required
              />
              <label htmlFor="coupon_code">From</label>
            </div>
            <div className="form-floating">
              <input
                type="date"
                className="form-control"
                value={customDates.to_date}
                onChange={(e) => setCustomDates({ ...customDates, to_date: e.target.value })}
                required
              />
              <label htmlFor="coupon_code">To</label>
            </div>
            <div className="mt-4">
              <button
                onClick={() => {
                  setShowCustomDatePopup(false);
                  fetchGraphEarnings('custom', customDates.from_date, customDates.to_date);
                }}
                className="btn btn-black w-100"
                style={{ marginRight: '10px' }}
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Earnings;
